import { useId, useState, type FormEvent } from 'react';
import { Badge, Button, Card, Field, Input, toast } from '@vwt/ui';
import { isApiError, useApiMutation } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { useMe, useSession } from '../../lib/session.js';
import { SectionTitle } from '../../components/common.js';

/** Profile card: linked accounts, and adding or removing one's own GitHub accounts (ADR-0011). */
export function LinkedAccounts() {
  const { t } = useTranslation();
  const me = useMe();
  const { refreshMe } = useSession();
  const inputId = useId();
  const [login, setLogin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const link = useApiMutation('linkMyGitHubAccount');
  const unlink = useApiMutation('unlinkMyIdentity');

  const add = async (event: FormEvent) => {
    event.preventDefault();
    if (!login.trim()) return;
    setError(null);
    try {
      await link.mutateAsync({ body: { login: login.trim() } });
      await refreshMe();
      setLogin('');
      toast.success(t('settings.github.added'));
    } catch (e) {
      const known = githubErrorKey(e);
      if (known) setError(t(known));
      else toastError(e);
    }
  };

  const remove = async (identityId: string) => {
    try {
      await unlink.mutateAsync({ params: { identityId } });
      await refreshMe();
      setRemoving(null);
      toast.success(t('settings.github.removed'));
    } catch (e) {
      toastError(e);
    }
  };

  return (
    <Card className="p-5">
      <SectionTitle>{t('settings.identities')}</SectionTitle>
      {me.identities.length ? (
        <ul className="mb-4 flex flex-col divide-y divide-border text-sm" aria-label={t('settings.identities')}>
          {me.identities.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2 py-2">
              <span className="font-medium">{i.provider === 'GITHUB' ? 'GitHub' : i.provider === 'JIRA' ? 'Jira' : i.provider}</span>
              <span className="min-w-0 truncate">{i.provider === 'GITHUB' && i.displayHandle ? `@${i.displayHandle}` : (i.displayHandle ?? i.externalAccountId)}</span>
              <Badge tone="neutral">{t(methodKey(i.verificationMethod))}</Badge>
              {i.provider === 'GITHUB' ? (
                <span className="ml-auto flex items-center gap-2">
                  {removing === i.id ? (
                    <>
                      <span className="text-xs text-fg-muted">{t('settings.github.confirmRemove')}</span>
                      <Button size="sm" variant="danger" loading={unlink.isPending} onClick={() => void remove(i.id)}>
                        {t('settings.github.remove')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(null)}>
                        {t('common.cancel')}
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(i.id)}>
                      {t('settings.github.remove')}
                    </Button>
                  )}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-sm text-fg-subtle">{t('settings.noIdentities')}</p>
      )}
      <form className="flex flex-col gap-2" onSubmit={(e) => void add(e)}>
        <Field label={t('settings.github.addLabel')} htmlFor={inputId} hint={t('settings.github.hint')} error={error ?? undefined}>
          <div className="flex gap-2">
            <Input id={inputId} value={login} onChange={(e) => (setLogin(e.target.value), setError(null))} placeholder={t('settings.github.placeholder')} maxLength={40} autoComplete="off" spellCheck={false} />
            <Button type="submit" variant="secondary" loading={link.isPending} disabled={!login.trim()}>
              {t('settings.github.add')}
            </Button>
          </div>
        </Field>
      </form>
    </Card>
  );
}

/** Translation key for how an account was linked. */
export function methodKey(method: string): string {
  switch (method) {
    case 'SELF_DECLARED':
      return 'settings.github.method.self';
    case 'GITHUB_VERIFIED_EMAIL':
      return 'settings.github.method.email';
    case 'ADMIN_ATTESTED':
      return 'settings.github.method.admin';
    default:
      return 'settings.github.method.other';
  }
}

/** Translated, field-level messages for the expected refusals. */
function githubErrorKey(error: unknown): string | null {
  if (!isApiError(error)) return null;
  const details = error.envelope?.error.details;
  if (error.code === 'INVALID_STATE' && details?.violations?.some((v) => v.code === 'GITHUB_ACCOUNT_TAKEN')) return 'settings.github.errors.taken';
  if (error.code === 'VALIDATION_FAILED') {
    const code = details?.fields?.find((f) => f.path === 'login')?.code;
    if (code === 'not_found') return 'settings.github.errors.notFound';
    if (code === 'not_a_user') return 'settings.github.errors.notUser';
    if (code === 'too_many') return 'settings.github.errors.tooMany';
  }
  if (error.code === 'CONNECTOR_UNAVAILABLE') return 'settings.github.errors.unavailable';
  return null;
}
